import { PublicKey } from '@solana/web3.js';
import {
	getUserAccountPublicKeySync,
	getUserStatsAccountPublicKey,
	UserStats,
} from '@velocity-exchange/sdk';
import { getUserKey } from '@velocity-exchange/common';
import useVelocityAccountsStore from 'src/stores/useVelocityAccountsStore';
import useVelocityClient from 'src/hooks/useVelocityClient';
import { useCallback } from 'react';
import { dlog } from 'src/dev';

/* Sets deleted subaccounts' pubkeys, so users can still export the history. Returns false if there are none */
export const useSyncDeletedSubaccountsPubkey = () => {
	const velocityClient = useVelocityClient();
	const accountsSet = useVelocityAccountsStore((s) => s.set);

	/**
	 * Sets deleted subaccounts' pubkeys, so users can still export the history. Returns false if there are none
	 */
	const syncDeletedSubaccountsPubkey = useCallback(
		(authority: PublicKey) => {
			const setNewDeletedKeys = (ids: number[]) => {
				accountsSet((s) => {
					s.deletedAccounts = ids.map((subAccountId) => {
						return {
							pubKey: getUserAccountPublicKeySync(
								velocityClient?.program.programId as PublicKey,
								authority,
								subAccountId
							),
							userId: subAccountId,
							userKey: getUserKey(subAccountId, authority),
							name: `Deleted Subaccount ${subAccountId}`,
						};
					});
				});
			};

			const createIdArray = (n: number): number[] =>
				Array.from({ length: n }, (_, i) => i);

			if (!authority || !velocityClient) {
				return false;
			}

			try {
				// if userStats already set, check it for deleted accounts. if not, try to search for it
				if (velocityClient.userStats && velocityClient.userStats.isSubscribed) {
					const statsAccount = velocityClient.userStats.getAccountOrThrow();
					const numAccountsCreated = statsAccount.numberOfSubAccountsCreated;
					const numAccountsCurrent = statsAccount.numberOfSubAccounts;

					if (numAccountsCreated == numAccountsCurrent) {
						return false;
					}

					// if more created than current, then they have some deleted. find the missing subaccount ids from their user list
					const subAccountIds = Array.from(velocityClient.users.values()).map(
						(user) => user.getUserAccountOrThrow().subAccountId
					);

					const deletedIds = createIdArray(numAccountsCreated).filter(
						(num) => !subAccountIds.includes(num)
					);

					setNewDeletedKeys(deletedIds);

					return true;
				}

				const userStatsAccountPublicKey = getUserStatsAccountPublicKey(
					velocityClient.program.programId,
					authority
				);

				const userStatsAccount = new UserStats({
					velocityClient: velocityClient,
					userStatsAccountPublicKey,
					accountSubscription: { type: 'websocket' },
				});

				// If no user stats account, they truly never used Velocity
				if (!userStatsAccount) {
					return false;
				}

				// Otherwise, they used Velocity but deleted all accounts
				const accountsCreated =
					userStatsAccount.getAccountOrThrow().numberOfSubAccountsCreated;

				setNewDeletedKeys(createIdArray(accountsCreated));

				return true;
			} catch (e) {
				dlog('deleted_accounts', `Error fetching deleted accounts: ${e}`);
				return false;
			}
		},
		[velocityClient, accountsSet]
	);

	return { syncDeletedSubaccountsPubkey };
};
